import { Symbol } from './symbol';

/**
 * The admin console's icon set, in **Material Symbols Rounded** — the same
 * family the three Flutter apps use, so a truck means the same thing and is
 * drawn the same way on every surface of the product.
 *
 * These are drop-in replacements for the `lucide-react` components the console
 * previously imported: same names, same `size` / `className` props. A page
 * migrates by changing one import line, not fifty call sites:
 *
 * ```diff
 * - import { Package, Truck } from 'lucide-react';
 * + import { Package, Truck } from '../../components/icons';
 * ```
 *
 * Icons here are decorative by default (`aria-hidden`) because every call site
 * pairs them with visible text. Where an icon stands alone, use `Symbol`
 * directly and pass `label`.
 */
interface IconProps {
  size?: number;
  className?: string;
}

/** Builds a lucide-compatible component bound to a Material Symbols ligature. */
function icon(name: string, opts: { fill?: boolean } = {}) {
  const Component = ({ size = 16, className }: IconProps) => (
    <Symbol name={name} size={size} fill={opts.fill} className={className} />
  );
  Component.displayName = `Symbol(${name})`;
  return Component;
}

// ── Status & feedback ───────────────────────────────────────────────────
export const AlertTriangle = icon('warning');
export const CheckCircle = icon('check_circle');
export const XCircle = icon('cancel');
export const Info = icon('info');
export const HelpCircle = icon('help');
export const Ban = icon('block');
export const Flag = icon('flag');
export const ShieldCheck = icon('verified_user');
export const Clock = icon('schedule');
export const Calendar = icon('calendar_month');

// ── Commerce ────────────────────────────────────────────────────────────
export const Package = icon('inventory_2');
export const ShoppingCart = icon('shopping_cart');
export const Store = icon('storefront');
export const Truck = icon('local_shipping');
export const IndianRupee = icon('currency_rupee');
export const Percent = icon('percent');
export const CreditCard = icon('credit_card');
export const Wallet = icon('account_balance_wallet');
// Filled: a star outline reads as "not rated" next to a rating value.
export const Star = icon('star', { fill: true });
export const ThumbsUp = icon('thumb_up');
export const TrendingUp = icon('trending_up');
export const TrendingDown = icon('trending_down');

// ── People ──────────────────────────────────────────────────────────────
export const User = icon('person');
export const Users = icon('group');
export const UserPlus = icon('person_add');

// ── Communication ───────────────────────────────────────────────────────
export const Mail = icon('mail');
export const Phone = icon('call');
export const Send = icon('send');
export const Bell = icon('notifications');
export const MessageSquare = icon('chat_bubble');

// ── System ──────────────────────────────────────────────────────────────
export const Globe = icon('language');
export const HardDrive = icon('storage');
export const KeyRound = icon('key');
export const Lock = icon('lock');
export const Save = icon('save');
export const RefreshCw = icon('refresh');
export const Trash2 = icon('delete');
export const MapPin = icon('location_on');
export const FileCheck = icon('task');
export const FileX = icon('file_present');
export const ImageOff = icon('hide_image');
export const Image = icon('image');
export const Video = icon('videocam');
export const Ruler = icon('straighten');
export const Eye = icon('visibility');
export const EyeOff = icon('visibility_off');

// ── Direction & chrome ──────────────────────────────────────────────────
export const ArrowRight = icon('arrow_forward');
export const ChevronLeft = icon('chevron_left');
export const ChevronRight = icon('chevron_right');
export const ChevronUp = icon('keyboard_arrow_up');
export const ChevronDown = icon('keyboard_arrow_down');
export const X = icon('close');
